import { z } from 'zod'

const branchCodeRegex = /^[A-Za-z0-9\s.,\-_()/#&]*$/
const phoneRegex = /^[0-9\-+()]*$/

export const storeFormSchema = z.object({
  business_unit_id: z.coerce.number().optional().nullable(),
  branch_code: z
    .string()
    .min(1, 'Branch code is required')
    .max(255)
    .regex(branchCodeRegex, 'Branch code contains invalid characters'),
  branch_name: z
    .string()
    .min(1, 'Branch name is required')
    .max(255)
    .regex(/^[^<>]*$/u, 'Branch name contains invalid characters'),
  branch_address: z
    .string()
    .max(2000)
    .regex(/^[^<>]*$/u, 'Branch address contains invalid characters')
    .optional()
    .or(z.literal('')),
  region_id: z.coerce.number().optional().nullable(),
  province_id: z.coerce.number().optional().nullable(),
  location_address: z.string().max(2000).optional().or(z.literal('')),
  normalized_address: z.string().max(2000).optional().or(z.literal('')),
  latitude: z.coerce
    .number({ invalid_type_error: 'Latitude is required' })
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),
  longitude: z.coerce
    .number({ invalid_type_error: 'Longitude is required' })
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),
  branch_email: z.string().min(1, 'Branch email is required').email('Please enter a valid email').max(255),
  contact_number: z
    .string()
    .max(20)
    .regex(phoneRegex, 'Contact number format is invalid')
    .optional()
    .or(z.literal('')),
  mobile_number: z
    .string()
    .max(20)
    .regex(phoneRegex, 'Mobile number format is invalid')
    .optional()
    .or(z.literal('')),
  contact_person: z
    .string()
    .max(255)
    .regex(branchCodeRegex, 'Contact person name contains invalid characters')
    .optional()
    .or(z.literal('')),
  active: z.boolean().default(true),
}).superRefine((data, ctx) => {
  const hasContactNumber = !!data.contact_number && data.contact_number.trim().length > 0
  const hasMobileNumber = !!data.mobile_number && data.mobile_number.trim().length > 0

  if (!hasContactNumber && !hasMobileNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['contact_number'],
      message: 'Contact Number or Mobile Number is required',
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['mobile_number'],
      message: 'Contact Number or Mobile Number is required',
    })
  }
})

export type StoreFormData = z.infer<typeof storeFormSchema>
